import { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { Box, Button, Stack, Typography, Paper } from '@mui/material';
import { Undo as UndoIcon, Delete as ClearIcon } from '@mui/icons-material';
import { ReactSketchCanvas, type ReactSketchCanvasRef } from 'react-sketch-canvas';
import { useTranslation } from 'react-i18next';

export interface SignaturePadRef {
  getSignature: () => Promise<string>;
  isEmpty: () => boolean;
  clear: () => void;
}

interface SignaturePadProps {
  onChange?: (hasSigned: boolean) => void;
  disabled?: boolean;
}

type SketchPath = Awaited<ReturnType<ReactSketchCanvasRef['exportPaths']>>[number];

const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  ({ onChange, disabled = false }, ref) => {
    const { t } = useTranslation();
    const canvasRef = useRef<ReactSketchCanvasRef>(null);
    const [hasStrokes, setHasStrokes] = useState(false);

    useImperativeHandle(ref, () => ({
      getSignature: async () => {
        if (!canvasRef.current) return '';
        return canvasRef.current.exportImage('png');
      },
      isEmpty: () => !hasStrokes,
      clear: () => {
        canvasRef.current?.clearCanvas();
        setHasStrokes(false);
        onChange?.(false);
      },
    }));

    const handleStroke = () => {
      if (!hasStrokes) {
        setHasStrokes(true);
        onChange?.(true);
      }
    };

    const handleClear = () => {
      canvasRef.current?.clearCanvas();
      setHasStrokes(false);
      onChange?.(false);
    };

    const handleUndo = () => {
      canvasRef.current?.undo();
      // Check if canvas still has strokes after undo
      canvasRef.current?.exportPaths().then((paths: SketchPath[]) => {
        const hasPaths = paths.length > 0;
        setHasStrokes(hasPaths);
        onChange?.(hasPaths);
      });
    };

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}
        >
          {t('document.signatureLabel')}
        </Typography>

        <Box
          sx={{
            border: '2px solid',
            borderColor: hasStrokes ? 'success.main' : 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            transition: 'border-color 0.2s ease',
            bgcolor: 'white',
            // Prevent parent scroll while drawing
            touchAction: 'none',
          }}
        >
          <ReactSketchCanvas
            ref={canvasRef}
            width="100%"
            height="160px"
            strokeWidth={2.5}
            strokeColor="#000"
            canvasColor="#fff"
            onStroke={handleStroke}
            style={{
              border: 'none',
              borderRadius: 0,
            }}
          />
        </Box>

        {/* Signature line indicator */}
        <Box
          sx={{
            mx: 2,
            mt: -0.5,
            mb: 1,
            borderBottom: '1px solid',
            borderColor: 'grey.400',
          }}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            startIcon={<UndoIcon />}
            onClick={handleUndo}
            disabled={!hasStrokes}
            sx={{ textTransform: 'none' }}
          >
            {t('document.signatureUndo')}
          </Button>
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            disabled={!hasStrokes}
            color="error"
            sx={{ textTransform: 'none' }}
          >
            {t('document.signatureClear')}
          </Button>
        </Stack>
      </Paper>
    );
  },
);

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
